import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router';
import type { PackageItemStatus } from '@pirouette/shared';
import { useDancers } from '@/data/dancers';
import { useDancerRecitalPackages } from '@/data/recitalPackages';
import { Empty } from '@/components/ui/Empty';
import { Badge } from '@/components/ui/Badge';

export const Route = createFileRoute('/parent/dancers')({
  component: ParentDancers,
});

function ParentDancers() {
  // This file is both the list (/parent/dancers) AND the parent of the detail
  // and videos child routes. Render <Outlet/> for any child path so those
  // routes actually mount; only render the list at the index path. Without
  // this, navigating to /parent/dancers/$id re-rendered the list. Mirrors the
  // faculty.classes.tsx pattern.
  const location = useLocation();
  const currentPath = location.pathname.replace(/\/+$/, '') || '/';
  const isDancersIndex = currentPath === '/' || currentPath === '/parent/dancers';

  // RLS filters server-side to this parent's family only.
  const dancers = useDancers();

  if (!isDancersIndex) {
    return <Outlet />;
  }

  return (
    <div className="space-y-8">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Your family
        </p>
        <h1 className="mt-2">Dancers</h1>
      </header>

      {dancers.isLoading && <p aria-live="polite">Loading…</p>}

      {dancers.data?.rows.length === 0 && (
        <Empty
          title="No dancers yet"
          description="Once your studio admin adds your dancers, they'll appear here."
        />
      )}

      {dancers.data && dancers.data.rows.length > 0 && (
        <ul className="grid gap-3 sm:grid-cols-2">
          {dancers.data.rows.map((d) => (
            <li key={d.id} className="surface-elevated p-5">
              <Link to="/parent/dancers/$id" params={{ id: d.id }} className="block">
                <p className="font-[var(--font-display)] text-[var(--text-2xl)]">
                  {d.preferred_name ?? d.first_name} {d.last_name}
                </p>
                <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  Born {d.date_of_birth}
                  {d.pronouns ? ` · ${d.pronouns}` : ''}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Badge tone={d.status === 'active' ? 'success' : 'neutral'}>{d.status}</Badge>
                  {d.costume_due && <Badge tone="warn">costume due</Badge>}
                  {d.payment_due && <Badge tone="danger">payment due</Badge>}
                </div>
                <PackageStatusSummary dancerId={d.id} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const fallbackFulfillmentFields = [
  'costume_status',
  'tshirt_status',
  'ticket_status',
  'video_status',
  'program_status',
] as const;

type FulfillmentField = (typeof fallbackFulfillmentFields)[number];

const statusLabels: Record<PackageItemStatus, string> = {
  not_started: 'not started',
  ordered: 'ordered',
  received: 'received',
  delivered: 'delivered',
  waived: 'waived',
};

function PackageStatusSummary({ dancerId }: { dancerId: string }) {
  const packages = useDancerRecitalPackages(dancerId);
  const assignment = packages.data?.[0];

  if (!assignment) return null;

  const fulfillmentFields = getIncludedFulfillmentFields(assignment.recital_packages);
  const completeCount = fulfillmentFields.filter((field) =>
    ['delivered', 'waived'].includes(assignment[field]),
  ).length;
  const nextStatus =
    fulfillmentFields
      .map((field) => assignment[field])
      .find((status) => !['delivered', 'waived'].includes(status)) ?? 'delivered';

  return (
    <div className="mt-4 border-t border-[var(--color-border)] pt-3">
      <p className="font-medium text-[var(--text-sm)]">
        {assignment.recital_packages?.name ?? 'Recital package'}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Badge tone={completeCount === fulfillmentFields.length ? 'success' : 'neutral'}>
          {completeCount}/{fulfillmentFields.length} fulfilled
        </Badge>
        <Badge tone={nextStatus === 'delivered' ? 'success' : 'warn'}>
          {statusLabels[nextStatus]}
        </Badge>
        {assignment.charge_id ? <Badge tone="success">charged</Badge> : <Badge>not charged</Badge>}
      </div>
    </div>
  );
}

function getIncludedFulfillmentFields(
  pkg:
    | {
        includes_costume: boolean;
        includes_tshirt: boolean;
        included_ticket_count: number;
        includes_video: boolean;
        includes_program: boolean;
      }
    | null
    | undefined,
): FulfillmentField[] {
  if (!pkg) return [...fallbackFulfillmentFields];

  return [
    pkg.includes_costume ? 'costume_status' : null,
    pkg.includes_tshirt ? 'tshirt_status' : null,
    pkg.included_ticket_count > 0 ? 'ticket_status' : null,
    pkg.includes_video ? 'video_status' : null,
    pkg.includes_program ? 'program_status' : null,
  ].filter((field): field is FulfillmentField => field !== null);
}
